/**
 * GuideBox — the shared shell for every onboarding guide surface
 * (``SectionGuide`` / ``ConnectorsIntro``): brand card + collapsible header +
 * expand/collapse content gating. Single source of the box chrome so the
 * surfaces can't drift (DRY). Callers pass the spacing variant via ``className``
 * and own their content's inner layout.
 */
import { cn } from "@ando/ui";
import type { LucideIcon } from "lucide-react";
import type { CSSProperties } from "react";

import { GuideBoxHeader } from "./GuideBoxHeader";

export { focusRing } from "./GuideBoxHeader";

/**
 * Warm accent border — thick rounded left edge fading to a hairline. Inlined
 * here (kept out of @ando/ui) so this working-area slice stays app-local.
 */
const accentBorder = {
  className: "rounded-xl border border-transparent border-l-[6px]",
  style: {
    background:
      "linear-gradient(162deg, color-mix(in srgb, #FF7A1A 14%, hsl(var(--card))) 0%, hsl(var(--card)) 66%) padding-box, " +
      "linear-gradient(135deg, #E8650E 0%, #FF7A1A 16%, hsl(var(--border)) 85%) border-box",
  } satisfies CSSProperties,
} as const;

export interface GuideBoxProps {
  readonly icon: LucideIcon;
  readonly title: React.ReactNode;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  /** Optional landmark label for the ``<section>`` (a11y). */
  readonly ariaLabel?: string;
  /** Outer wrapper classes (margins). Colours/border are fixed; the INNER
   *  padding lives on the header/body so the header hover spans the full card. */
  readonly className?: string;
  /** Quiet variant: a plain hairline box instead of the warm orange accent
   *  border — used where the guide should not compete for attention (e.g. the
   *  collapsed Integrations explainer). The small brand icon in the header
   *  stays, so it still reads as a guide. */
  readonly quiet?: boolean;
  /** Header inner padding (clickable row). */
  readonly headerPadding?: string;
  /** Expanded-body inner padding (no top: the header's bottom pad is the gap). */
  readonly bodyPadding?: string;
  readonly children: React.ReactNode;
}

export function GuideBox({
  icon,
  title,
  collapsed,
  onToggle,
  ariaLabel,
  className,
  quiet = false,
  headerPadding = "px-4 py-3",
  bodyPadding = "px-4 pb-4",
  children,
}: GuideBoxProps): React.ReactElement {
  // The card chrome is the shared ``accentBorder`` frame (thick rounded left
  // edge fading to a hairline toward the right) — or, when ``quiet``, a plain
  // hairline box. ``relative`` anchors inner layers; ``overflow-hidden`` clips
  // the header hover + open/close animation to the rounded corners. Same applies
  // everywhere this primitive is used (SectionGuide on every workspace tab +
  // ConnectorsIntro).
  return (
    <section
      aria-label={ariaLabel}
      style={quiet ? undefined : accentBorder.style}
      className={cn(
        "relative overflow-hidden",
        quiet ? "rounded-xl border border-hairline bg-card/60" : accentBorder.className,
        className,
      )}
    >
      <GuideBoxHeader
        icon={icon}
        title={title}
        collapsed={collapsed}
        onToggle={onToggle}
        padding={headerPadding}
      />
      {/* Smooth open/close via the grid-rows 0fr↔1fr trick (no height math).
          ``inert`` + ``aria-hidden`` keep the collapsed content out of tab order
          and the a11y tree while it's hidden. */}
      <div
        inert={collapsed}
        aria-hidden={collapsed}
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          collapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className={bodyPadding}>{children}</div>
        </div>
      </div>
    </section>
  );
}

GuideBox.displayName = "GuideBox";
